'use client';

import { useTheme } from '@emotion/react';
import { useEffect, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { Layout } from '../../components/layout/Layout';
import { Container } from '../../components/layout/Container';
import { H1, H2 } from '../../components/styled/typography';
import { DEVICE_SCREENS_MAX_STRING } from '../../lib/consts';
import { BreadCrumbs } from '../../components/blocks/BreadCrumbs';
import { City, Social, Page as IPage, Action } from '../../models';
import { Contact } from '../../models/contact';
import {
  FavoriteKinds,
  FavoriteKindsV5,
  FavoriteV5,
} from '../../models/favorite';
import { useFavorite } from '../../providers/favorite';
import { PostCard } from '../../components/posts/post-card';
import { PersonCard } from '../../components/person/person-card';
import { PlaceCard } from '../../components/places/place-card';
import { MaterialCard } from '../../components/material/material-card';
import { Viewed } from '../../components/viewed/viewed';
import { Subscription } from '../../components/about/Subscription';
import { MobileApps } from '../../components/about/MobileApps';
import ActionsItemFavorite from '../../components/actions/ActionsItemFavorite';
import { UpButton } from '../../components/common/UpButton';
import { Category } from '../../models/categories';
import { getLocalStorageItem } from '../../lib/api/browserStorage';
import { PersonInListV3 } from '../../models/person';
import { http } from '../../lib/api/http';
import { V5 } from '../../lib/api/urls';
import { ButtonShowMore } from '../../components/Buttons';
import { StandardButton } from '../../components/styled/Button';
import { useLoading } from '../../providers/loading';

const FAVORITES_PER_PAGE = 12;

const emptyTextByKind = {
  title: {
    event: 'одного события',
    place: 'одного места',
    person: 'одной персоны',
    material: 'одного материала',
  },
  content: {
    event: 'мероприятия',
    place: 'места',
    person: 'персоны',
    material: 'материала',
  },
};

function PageInnerComponent({
  currentCity,
  gorbiletRekomenduet,
  kind,
}: {
  currentCity: City;
  gorbiletRekomenduet: { results: Action[] };
  kind?: keyof typeof FavoriteKinds;
}) {
  const theme = useTheme();
  const router = useRouter();
  const pathname = usePathname();
  const [favoritesList, setFavoritesList] = useState<{
    next: string | null;
    count: number;
    results: FavoriteV5[];
  } | null>(null);
  const [page, setPage] = useState<number>(0);
  const [isLoadingFavorites, setIsLoadingFavorites] = useState(false);
  const { updateLoading } = useLoading();

  const favoritiesContext = useFavorite();

  function onSelectKind(newKind: keyof typeof FavoriteKinds) {
    router.replace(`${pathname}?kind=${newKind}`);
  }

  useEffect(() => {
    if (!kind) {
      router.replace(`${pathname}?kind=${FavoriteKinds.event}`);
    }
    setFavoritesList(null);
    setPage(0);
  }, [kind, router]);

  const fetchFavorites = ({
    pageNumber,
    favoriteKind,
  }: {
    pageNumber: number;
    favoriteKind: keyof typeof FavoriteKinds;
  }) => {
    if (kind) {
      setIsLoadingFavorites(true);
      http
        .get(
          V5.favorites,
          {
            brief: true,
            limit: FAVORITES_PER_PAGE,
            offset: pageNumber * FAVORITES_PER_PAGE,
            content_type: FavoriteKindsV5[favoriteKind],
          },
          null,
          { contentType: 'json', withTokenRequest: true }
        )
        .then((res) => {
          if (pageNumber > 0 && favoritesList) {
            setFavoritesList({
              ...res,
              results: favoritesList.results.concat(res.results),
            });
          } else {
            setFavoritesList(res);
          }
        })
        .finally(() => setIsLoadingFavorites(false));
    }
  };

  useEffect(() => {
    updateLoading(isLoadingFavorites);
  }, [isLoadingFavorites]);

  useEffect(() => {
    if (kind) {
      fetchFavorites({ pageNumber: page, favoriteKind: kind });
    }
  }, [page, kind]);

  useEffect(() => {
    if (favoritiesContext && favoritesList && kind) {
      const idsByKind = Object.keys(favoritiesContext.getFilteredList(kind));
      if (idsByKind.length !== favoritesList.count) {
        fetchFavorites({ pageNumber: 0, favoriteKind: kind });
      }
    }
  }, [favoritiesContext, favoritesList, kind]);

  if (!favoritiesContext || !kind) {
    return null;
  }
  const isHaveFavItemsForSelectedKind =
    !!favoritiesContext.getFilteredList(kind) &&
    Object.values(favoritiesContext.getFilteredList(kind)).length > 0;

  return (
    <Container
      css={{
        padding: '14px 8px',
        [DEVICE_SCREENS_MAX_STRING.md]: {
          padding: '14px 8px',
        },
      }}
      className='container'
    >
      <BreadCrumbs currentCity={currentCity} items={[{ title: 'Избранное' }]} />
      <H1
        css={{
          fontFamily: 'Montserrat',
          color: '#282828',
          fontWeight: 700,
          marginTop: 20,
          [DEVICE_SCREENS_MAX_STRING.md]: {
            fontSize: 32,
            marginTop: 12,
          },
          [DEVICE_SCREENS_MAX_STRING.sm]: {
            fontSize: 22,
            lineHeight: '26px',
          },
          marginBottom: '4rem',
        }}
      >
        Избранное
      </H1>
      <main css={{ fontFamily: 'Golos', color: theme.colors.black }}>
        <UpButton screenCount={1.5} />
        <div
          css={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div css={{ marginBottom: 50 }}>
            <div
              css={{
                ...theme.mixins.fonts.golos,
                color: theme.colors.text,
                display: 'flex',
                gap: 10,
                marginBottom: 20,
                div: {
                  cursor: 'pointer',
                },
              }}
            >
              <div
                css={
                  kind === FavoriteKinds.event
                    ? { color: theme.colors.red }
                    : {}
                }
                onClick={() => onSelectKind(FavoriteKinds.event)}
              >
                События
              </div>
              <div
                css={
                  kind === FavoriteKinds.place
                    ? { color: theme.colors.red }
                    : {}
                }
                onClick={() => onSelectKind(FavoriteKinds.place)}
              >
                Места
              </div>
              <div
                css={
                  kind === FavoriteKinds.person
                    ? { color: theme.colors.red }
                    : {}
                }
                onClick={() => onSelectKind(FavoriteKinds.person)}
              >
                Персоны
              </div>
              <div
                css={
                  kind === FavoriteKinds.material
                    ? { color: theme.colors.red }
                    : {}
                }
                onClick={() => onSelectKind(FavoriteKinds.material)}
              >
                Материалы блога
              </div>
            </div>
            <div
              css={{
                display: 'flex',
                flexWrap: 'wrap',
                width: '100%',
                [DEVICE_SCREENS_MAX_STRING.sm]: {
                  flexDirection: 'column',
                },
              }}
              // favorites-page__actions actions
            >
              {isHaveFavItemsForSelectedKind &&
                kind === FavoriteKinds.event &&
                favoritesList &&
                favoritesList.results.map((favItem) => (
                  <ActionsItemFavorite key={favItem.content_object.id}>
                    <PostCard
                      withOutWrapper
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore
                      item={favItem.content_object}
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore
                      href={[
                        '',
                        'city' in favItem.content_object &&
                          favItem.content_object.city.url,
                        'actions',
                        favItem.content_object.slug,
                        '',
                      ].join('/')}
                    />
                  </ActionsItemFavorite>
                ))}
              {isHaveFavItemsForSelectedKind &&
                kind === FavoriteKinds.place &&
                favoritesList &&
                favoritesList.results.map((favItem) => (
                  <ActionsItemFavorite key={favItem.content_object.id}>
                    {/* eslint-disable-next-line */}
                    {/* @ts-ignore */}
                    <PlaceCard place={favItem.content_object} />
                  </ActionsItemFavorite>
                ))}
              {isHaveFavItemsForSelectedKind &&
                kind === FavoriteKinds.person &&
                favoritesList &&
                favoritesList.results.map((favItem) => (
                  <ActionsItemFavorite key={favItem.content_object.id}>
                    <PersonCard
                      item={favItem.content_object as unknown as PersonInListV3}
                    />
                  </ActionsItemFavorite>
                ))}
              {isHaveFavItemsForSelectedKind &&
                kind === FavoriteKinds.material &&
                favoritesList &&
                favoritesList.results.map((favItem) => (
                  <ActionsItemFavorite key={favItem.content_object.id}>
                    <MaterialCard
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore
                      item={favItem.content_object}
                      fromFavorite
                    />
                  </ActionsItemFavorite>
                ))}
            </div>
            {favoritesList && favoritesList.next && (
              <div
                css={{
                  width: '100%',
                  display: 'flex',
                  gap: 10,
                  padding: '0 8px',
                  [DEVICE_SCREENS_MAX_STRING.md]: {
                    paddingTop: 10,
                  },
                }}
              >
                <ButtonShowMore
                  callback={() => setPage(page + 1)}
                  loadingText={isLoadingFavorites ? 'Загрузка...' : ''}
                />
                <StandardButton callback={() => window.scrollTo(0, 0)}>
                  Наверх
                </StandardButton>
              </div>
            )}
            {!isHaveFavItemsForSelectedKind && (
              <div
                css={{
                  margin: '0 auto',
                  textAlign: 'center',
                  width: '100%',
                }}
              >
                <H2
                  css={{
                    color: theme.colors.text,
                    fontFamily: 'Montserrat',
                    fontSize: 26,
                    marginTop: '0.83em',
                    marginBottom: '0.83em',
                  }}
                >
                  Вы еще не добавили ни {emptyTextByKind.title[kind]} в
                  избранное
                </H2>
                <div
                  css={{
                    ...theme.mixins.fonts.golos,
                    margin: '0 auto',
                    width: '40%',
                    [DEVICE_SCREENS_MAX_STRING.sm]: {
                      width: '100%',
                    },
                  }}
                >
                  Нажимайте на иконку{' '}
                  <svg
                    css={{
                      marginLeft: 2,
                      marginRight: 5,
                      width: 16,
                      height: 16,
                      fill: theme.colors.red,
                    }}
                  >
                    <use xlinkHref='#like2' />
                  </svg>
                  на карточке {emptyTextByKind.content[kind]}, чтобы добавить
                  его в «Избранное» и не пропустить
                </div>
              </div>
            )}
          </div>
        </div>
        <Viewed title='Горбилет рекомендует' viewed={gorbiletRekomenduet} />
        <div
          // page__misc
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          css={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 16,
            marginBottom: '4rem',
            [DEVICE_SCREENS_MAX_STRING.xl]: {
              marginBottom: '2rem',
              gridTemplateColumns: '1fr',
            },
          }}
        >
          <div
            css={{
              paddingLeft: theme.constants.padding,
              paddingRight: theme.constants.padding,
            }}
            // service__sub
          >
            <Subscription currentCity={currentCity} />
          </div>
          <div
            css={{
              paddingLeft: theme.constants.padding,
              paddingRight: theme.constants.padding,
            }}
            // service__app
          >
            <MobileApps />
          </div>
        </div>
      </main>
    </Container>
  );
}

export function PageComponent({
  currentCity,
  cities,
  gorbiletRekomenduet,
  categoriesForHeader,
  footerProps,
  query,
}: {
  currentCity: City;
  cities: { results: City[] };
  gorbiletRekomenduet: { results: Action[] };
  categoriesForHeader: Category[];
  query: { kind?: keyof typeof FavoriteKinds };
  footerProps: {
    pageList: { results: IPage[] };
    socials: { results: Social[] };
    socialsLinks: { results: Social[] };
    contactList: { results: Contact[] };
  };
}) {
  const [city, setCity] = useState<City>(currentCity);

  useEffect(() => {
    const lsCity = cities.results.find(
      (item) => item.url === getLocalStorageItem('current-city')
    );

    if (lsCity) {
      setCity(lsCity);
    }
  }, []);

  return (
    <Layout
      categoriesForHeader={categoriesForHeader}
      footerProps={footerProps}
      currentCity={city}
    >
      <PageInnerComponent
        currentCity={currentCity}
        gorbiletRekomenduet={gorbiletRekomenduet}
        kind={query.kind}
      />
    </Layout>
  );
}
